/**
 * LYT Communications - Change Order Manager
 * Version: 1.0.0
 * Created: 2026-02-23
 *
 * Track scope changes — added/removed items, financial impact, approval workflow.
 * Embedded in AdminProjectDashboard or ProjectMapPage.
 */

import React, { useState, useCallback } from 'react';
import { FileText, Plus, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, X, DollarSign } from 'lucide-react';

const CO_STATUSES = ['Draft', 'Submitted', 'Approved', 'Rejected'];

function ChangeOrderManager({ projectId, changeOrders = [], darkMode, user, onCreateCO, onApproveCO, onRejectCO }) {
  const [expanded, setExpanded] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    description: '',
    reason: '',
    line_items_json: '[]',
    original_value: '',
    revised_value: '',
  });

  const isAdmin = user?.role === 'Admin' || user?.role === 'admin' || user?.role === 'Supervisor';

  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';
  const inputBg = darkMode ? '#0d1b2a' : '#ffffff';

  const projectCOs = changeOrders.filter(co => co.project_id === projectId);
  const totalDelta = projectCOs.filter(co => co.status === 'Approved').reduce((sum, co) => sum + (parseFloat(co.delta) || 0), 0);

  const handleCreate = useCallback(async () => {
    if (!form.description.trim()) return;
    const original = parseFloat(form.original_value) || 0;
    const revised = parseFloat(form.revised_value) || 0;
    await onCreateCO?.({
      project_id: projectId,
      description: form.description,
      reason: form.reason,
      line_items_json: form.line_items_json,
      original_value: String(original),
      revised_value: String(revised),
      delta: String(revised - original),
      status: 'Draft',
      created_by: user?.email || '',
    });
    setForm({ description: '', reason: '', line_items_json: '[]', original_value: '', revised_value: '' });
    setShowForm(false);
  }, [form, projectId, user, onCreateCO]);

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: `1px solid ${borderColor}`, background: inputBg, color: text,
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };

  const STATUS_COLORS = { 'Draft': '#9E9E9E', 'Submitted': '#FFB800', 'Approved': '#4CAF50', 'Rejected': '#FF4444' };

  return (
    <div style={{ background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
      <div onClick={() => setExpanded(!expanded)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', cursor: 'pointer', borderBottom: expanded ? `1px solid ${borderColor}` : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileText size={16} color={accent} />
          <span style={{ fontWeight: 700, fontSize: 14, color: text }}>Change Orders</span>
          {projectCOs.length > 0 && (
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600, background: totalDelta >= 0 ? '#4CAF5020' : '#FF444420', color: totalDelta >= 0 ? '#4CAF50' : '#FF4444' }}>
              {totalDelta >= 0 ? '+' : ''}{totalDelta.toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: textMuted }}>{projectCOs.length} CO{projectCOs.length !== 1 ? 's' : ''}</span>
          {expanded ? <ChevronUp size={14} color={textMuted} /> : <ChevronDown size={14} color={textMuted} />}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '10px 14px' }}>
          {projectCOs.length === 0 && !showForm && (
            <div style={{ fontSize: 12, color: textMuted, textAlign: 'center', padding: '12px 0' }}>
              No change orders for this project.
            </div>
          )}

          {projectCOs.map((co, idx) => {
            const delta = parseFloat(co.delta) || 0;
            return (
              <div key={co.co_id || idx} style={{ padding: '10px 12px', borderRadius: 8, border: `1px solid ${borderColor}`, marginBottom: 6, background: darkMode ? '#0d1b2a' : '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: text }}>{co.co_id}</span>
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, fontWeight: 600, background: `${STATUS_COLORS[co.status] || '#9E9E9E'}20`, color: STATUS_COLORS[co.status] || '#9E9E9E' }}>{co.status}</span>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: delta >= 0 ? '#4CAF50' : '#FF4444' }}>
                    {delta >= 0 ? '+' : ''}{delta.toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: text, marginBottom: 2 }}>{co.description}</div>
                {co.reason && <div style={{ fontSize: 11, color: textMuted, fontStyle: 'italic' }}>Reason: {co.reason}</div>}

                {isAdmin && co.status === 'Submitted' && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <button onClick={() => onApproveCO?.(co.co_id)} style={{ flex: 1, padding: '4px 8px', fontSize: 11, fontWeight: 600, background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      <CheckCircle size={12} /> Approve
                    </button>
                    <button onClick={() => onRejectCO?.(co.co_id)} style={{ flex: 1, padding: '4px 8px', fontSize: 11, fontWeight: 600, background: '#FF4444', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      <XCircle size={12} /> Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {showForm ? (
            <div style={{ padding: 12, borderRadius: 8, border: `1px solid ${accent}40`, background: darkMode ? '#0d1b2a' : '#f0f9ff', marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: text }}>New Change Order</span>
                <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X size={14} color={textMuted} /></button>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Description *</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe the scope change..." rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Reason</label>
                <input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Why is this change needed?" style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Original Value ($)</label>
                  <input type="number" value={form.original_value} onChange={e => setForm({ ...form, original_value: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Revised Value ($)</label>
                  <input type="number" value={form.revised_value} onChange={e => setForm({ ...form, revised_value: e.target.value })} style={inputStyle} />
                </div>
              </div>
              <button onClick={handleCreate} disabled={!form.description.trim()} style={{ width: '100%', padding: '8px', fontSize: 13, fontWeight: 600, background: form.description.trim() ? accent : '#9E9E9E', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                Save Change Order
              </button>
            </div>
          ) : isAdmin && (
            <button onClick={() => setShowForm(true)} style={{ width: '100%', padding: '8px', fontSize: 12, fontWeight: 600, background: 'transparent', color: accent, border: `1px dashed ${accent}40`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 6 }}>
              <Plus size={14} /> Add Change Order
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ChangeOrderManager;
